import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { FormField } from '@/components/forms/FormField';
import { InvoiceTemplatePreview } from '@/components/invoice/InvoiceTemplatePreview';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { useSignatures } from '@/composables/shared/useSignatures';
import { organizationSettingsService } from '@/services/organizationSettings.service';
import { signatureService } from '@/services/signature.service';
import { quoteSettingsSchema, type QuoteSettingsFormData } from '@/lib/validators';
import type { OrganizationSettings } from '@/types/table';
import { NumberPreview } from './_shared';

/** Quote tab — quotation numbering marker, validity window, footer, live preview. */
export function QuoteTab() {
  const { settings, update } = useOrganizationSettings();
  const { defaultSignature } = useSignatures();

  const form = useForm<QuoteSettingsFormData>({
    resolver: zodResolver(quoteSettingsSchema),
    defaultValues: {
      quotationPrefix: 'QUO',
      nextQuotationSequence: 1,
      quotationFooterText: 'We look forward to working with you!',
      quotationDefaultValidityDays: 30,
    },
  });

  useEffect(() => {
    if (!settings) return;
    form.reset({
      quotationPrefix: settings.quotationPrefix ?? 'QUO',
      nextQuotationSequence: settings.nextQuotationSequence ?? 1,
      quotationFooterText: settings.quotationFooterText ?? '',
      quotationDefaultValidityDays: settings.quotationDefaultValidityDays ?? 30,
    });
  }, [settings, form]);

  const values = form.watch();
  const cacheBuster = settings ? new Date(settings.updatedAt).getTime() : 0;
  const logoUrl = settings?.logoFileKey ? organizationSettingsService.assetUrl('logo', cacheBuster) : null;
  const signatureUrl = defaultSignature ? signatureService.assetUrl(defaultSignature.id, new Date(defaultSignature.updatedAt).getTime()) : null;

  const onSubmit = (data: QuoteSettingsFormData) => update.mutate(data as Partial<OrganizationSettings>);

  const previewProps = useMemo(() => ({
    kind: 'quotation' as const,
    primaryColor: settings?.primaryColor || '#010b17',
    accentColor: settings?.accentColor || '#a855f7',
    highlightColor: settings?.highlightColor || '#22d3ee',
    companyName: settings?.companyName ?? '',
    companyEmail: settings?.companyEmail,
    companyPhone: settings?.companyPhone,
    companyWebsite: settings?.companyWebsite,
    taxId: settings?.taxId,
    addressLine1: settings?.addressLine1,
    addressLine2: settings?.addressLine2,
    city: settings?.city,
    state: settings?.state,
    country: settings?.country,
    postalCode: settings?.postalCode,
    signatoryName: defaultSignature?.signatoryName,
    signatoryTitle: defaultSignature?.signatoryTitle ?? undefined,
    invoiceFooterText: values.quotationFooterText,
    logoUrl,
    signatureUrl,
  }), [settings, defaultSignature, values, logoUrl, signatureUrl]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="grid gap-6 xl:grid-cols-[1fr_minmax(0,480px)]">
        <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quotation Numbering</CardTitle>
              <CardDescription>
                Format: <code className="text-foreground">{'{companyPrefix}-{marker}-{year}-{seq}'}</code>.
                Company prefix (<span className="font-medium text-foreground">{settings?.companyPrefix || 'none'}</span>) is set on the General tab.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField form={form} name="quotationPrefix" label="Quotation Type Marker" description="e.g. QT or QUO" />
                <FormField form={form} name="nextQuotationSequence" label="Next Quotation Number" type="number" description="Auto-bumps after each quotation." />
              </div>
              <NumberPreview kind="Quotation" company={settings?.companyPrefix || ''} marker={values.quotationPrefix || 'QUO'} seq={Number(values.nextQuotationSequence) || 1} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Validity & Footer</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <FormField form={form} name="quotationDefaultValidityDays" label="Default Validity (days)" type="number" description="Days from issue date used to compute 'Valid Until' on new quotations." />
              <FormField form={form} name="quotationFooterText" label="Quotation Footer" placeholder="We look forward to working with you!" />
            </CardContent>
          </Card>

          <Separator />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => form.reset()}>Reset</Button>
            <Button type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save Quote Settings'}</Button>
          </div>
        </div>

        <div className="min-w-0">
          <div className="sticky top-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Live Preview</CardTitle><CardDescription>How the generated quotation PDF will look</CardDescription></CardHeader>
              <CardContent className="bg-slate-100 dark:bg-slate-900 rounded-b-lg pt-4 pb-6">
                <InvoiceTemplatePreview {...previewProps} />
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground mt-2 px-1">Branding (logo, theme, company info) comes from the General tab. Edit it there to update this preview.</p>
          </div>
        </div>
      </div>
    </form>
  );
}
